import { useEffect, useLayoutEffect, useRef } from "react";
import { useLocation, useNavigationType } from "react-router-dom";

type ScrollPosition = {
  x: number;
  y: number;
};

const getLocationKey = (location: {
  key: string;
  pathname: string;
  search: string;
  hash: string;
}) => location.key || `${location.pathname}${location.search}${location.hash}`;

const MAX_RESTORE_ATTEMPTS = 30;

export const ScrollRestorationManager = () => {
  const location = useLocation();
  const navigationType = useNavigationType();

  const savedPositionsRef = useRef<Record<string, ScrollPosition>>({});
  const activeLocationKeyRef = useRef(getLocationKey(location));

  useLayoutEffect(() => {
    if (!("scrollRestoration" in window.history)) {
      return;
    }

    const previousValue = window.history.scrollRestoration;
    window.history.scrollRestoration = "manual";

    return () => {
      window.history.scrollRestoration = previousValue;
    };
  }, []);

  useEffect(() => {
    const saveCurrentPosition = () => {
      savedPositionsRef.current[activeLocationKeyRef.current] = {
        x: window.scrollX,
        y: window.scrollY,
      };
    };

    window.addEventListener("scroll", saveCurrentPosition, { passive: true });
    window.addEventListener("beforeunload", saveCurrentPosition);

    return () => {
      saveCurrentPosition();
      window.removeEventListener("scroll", saveCurrentPosition);
      window.removeEventListener("beforeunload", saveCurrentPosition);
    };
  }, []);

  useLayoutEffect(() => {
    const previousKey = activeLocationKeyRef.current;
    const nextKey = getLocationKey(location);

    savedPositionsRef.current[previousKey] = {
      x: window.scrollX,
      y: window.scrollY,
    };

    activeLocationKeyRef.current = nextKey;

    const savedPosition = savedPositionsRef.current[nextKey];

    const rootStyle = document.documentElement.style;
    const previousScrollBehavior = rootStyle.scrollBehavior;
    rootStyle.scrollBehavior = "auto";

    if (navigationType === "POP" && savedPosition) {
      let attempts = 0;

      const restore = () => {
        window.scrollTo(savedPosition.x, savedPosition.y);

        const maxScrollY =
          document.documentElement.scrollHeight - window.innerHeight;
        const reachedTarget =
          Math.abs(window.scrollY - savedPosition.y) <= 1 ||
          savedPosition.y <= maxScrollY;

        attempts += 1;

        if (!reachedTarget && attempts < MAX_RESTORE_ATTEMPTS) {
          requestAnimationFrame(restore);
        } else {
          rootStyle.scrollBehavior = previousScrollBehavior;
        }
      };

      requestAnimationFrame(restore);
    } else if (navigationType === "PUSH") {
  window.scrollTo(0,0);
  console.log("NAV TYPE:", navigationType);
}
  }, [location, navigationType]);

  return null;
};